import { useCallback, useMemo, useRef, useState } from "react";

export function usePullToRefresh({
  enabled = true,
  isBlocked = false,
  startZoneY = 260,
  maxPull = 110,
  readyPull = 70,
  refreshHeight = 56,
  onRefresh,
}) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startYRef = useRef(null);
  const activeRef = useRef(false);
  const lockRef = useRef(false);

  const height = useMemo(() => {
    if (refreshing) return refreshHeight;
    return Math.min(maxPull, pull);
  }, [refreshing, refreshHeight, maxPull, pull]);

  const ready = useMemo(() => {
    if (refreshing) return false;
    return pull >= readyPull;
  }, [pull, readyPull, refreshing]);

  const reset = useCallback(() => {
    setPull(0);
    startYRef.current = null;
    activeRef.current = false;
  }, []);

  const doRefresh = useCallback(async () => {
    if (refreshing) return;
    if (typeof onRefresh !== "function") return;

    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
      setPull(0);
    }
  }, [onRefresh, refreshing]);

  const onTouchStart = useCallback(
    (e) => {
      if (!enabled) return;
      if (isBlocked) return;
      if (lockRef.current) return;
      if (refreshing) return;
      if (window.scrollY > 0) return;

      const t = e.touches?.[0];
      if (!t) return;

      if (t.clientY > startZoneY) return;

      startYRef.current = t.clientY;
      activeRef.current = true;
    },
    [enabled, isBlocked, refreshing, startZoneY]
  );

  const onTouchMove = useCallback(
    (e) => {
      if (!activeRef.current) return;
      if (!enabled) return;
      if (isBlocked) return;
      if (refreshing) return;

      if (window.scrollY > 0) {
        reset();
        return;
      }

      const t = e.touches?.[0];
      if (!t) return;

      const startY = startYRef.current;
      if (startY == null) return;

      const dy = t.clientY - startY;
      if (dy <= 0) {
        setPull(0);
        return;
      }

      const eased = Math.min(maxPull, dy * 0.55);
      setPull(eased);

      // precisa para segurar o scroll “elástico”
      e.preventDefault?.();
    },
    [enabled, isBlocked, refreshing, maxPull, reset]
  );

  const onTouchEnd = useCallback(async () => {
    if (!activeRef.current) return;
    activeRef.current = false;
    startYRef.current = null;

    if (!enabled || isBlocked || refreshing) {
      setPull(0);
      return;
    }

    const currentPull = pull;

    if (currentPull >= readyPull) {
      lockRef.current = true;
      try {
        await doRefresh();
      } finally {
        // pequeno lock para evitar double-trigger
        setTimeout(() => {
          lockRef.current = false;
        }, 600);
      }
    } else {
      setPull(0);
    }
  }, [enabled, isBlocked, refreshing, pull, readyPull, doRefresh]);

  return {
    height,
    pull,
    ready,
    refreshing,
    handlers: { onTouchStart, onTouchMove, onTouchEnd },
  };
}
